<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('landlord.landlords') }}: {{ product.name }}</h1>
      <div class="details-component__header_right">
        <a v-if="!isFormOpen" class="link" v-on:click="toggleForm">
          {{ $t('landlord.add_landlord') }}
          <i class="icon icon--plus"></i>
        </a>
        <a v-else class="disabled link" v-on:click="toggleForm">{{ $t('common.close') }}
          <i class="icon icon--cross"></i>
        </a>
      </div>
    </div>

    <autocomplete class="details-component__lookupform inline-search"
      v-bind:class="{ active: isActive }"
      lookup="landlords"
      :filter="autocompleteLandlordFilter"
      v-on:query="selectLandlord" v-if="isFormOpen">
      <template slot="inner-item" slot-scope="props">
        <span>{{props.item.name}}</span>
        <span class="disabled">{{props.item.address}}</span>
      </template>
    </autocomplete>

    <div class="details-component__lookupform details-component__lookupform-confirm"
      v-if="isFormOpen && landlord !== null"
      v-bind:class="{ active: isActive}">
      <form class="form">
         <div class="form__field">
          <label class="form__label">{{ $t('common.landlord') }}</label>
          <div>{{landlord.name}}</div>
         </div>

         <div class="form__field" :class="{'form__field_error' : errors.has('amount')}" v-if="product.type === 'permitPack'">
          <label for="amount" class="form__label">{{ $t('common.amount') }}</label>
          <input type="text"
            id="amount"
            class="form__input"
            v-model="form.amount"
            v-validate
            data-vv-rules="required|numeric|max:4|cannotBeLower"
            name="amount"
            ref="amount"
             />
         </div>

         <div class="form__buttons buttonset">
           <button
             type="button"
             class="buttonset__button btn btn--secondary"
             v-on:click.prevent.stop="toggleForm()">
             {{ $t('common.discard') }}
           </button>
           <button type="submit"
              class="buttonset__button btn btn--main"
              v-on:click.prevent.stop="addLandlord()">
              {{ $t('tenants.submit') }}
          </button>
        </div>

      </form>
    </div>

    <div class="details-component__content content" v-if="product.shared">
      <v-client-table class="mobile-650" :data="product.shared" :columns="columns" :options="options">
          <span slot="name" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('common.name') }}: </span>
              <a class="link" @click="routeToLandlord(props.row)">
                  <span>{{props.row.name}}</span>
              </a>
          </span>
          <span slot="contactPerson" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('tenants.contact_person') }}: </span><span class="total">{{ props.row.contactPerson }}</span></span>
          <span slot="address" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('common.address') }}: </span><span class="total">{{ props.row.address }}</span></span>
          <div slot="removeCompany" slot-scope="props" class="delete-wrapper">
              <a @click="showConfirmationForRemovingLandlord(props.row)">
                  <span class="delete-wrapper__icon icon icon--cross"></span>
              </a>
          </div>
          <div slot="issued" slot-scope="props">
              <span class="mobile">{{ $t('common.issued') }}: </span>
              <span class="total-permit-count" v-show="!isSelectedRow(props.row.id)" @click="setSelectedRow(props.row)">
                {{props.row.issued}}
                <a class="edit-wrapper">
                    {{ $t('common.change') }}
                    <i class="icon icon--arrow" />
                </a>
              </span>
              <form class="form" v-show="isSelectedRow(props.row.id)">
                <input @input=" e => editTotalPermitAmount('permitInput', e.target.value)"
                       class="form__input--editing"
                       :value="props.row.issued"
                       name="permit_input"
                       :ref="'changeIssued' + props.row.id"
                       @keydown.prevent.stop.enter="submitEdit(props.row.id)"/>
                <div class="form__submit-edit" @click="submitEdit(props.row.id)"></div>
              </form>
            </div>
      </v-client-table>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelLandlordRemove" @confirmed="removeShared">
      <h1 slot="header">{{ $t('landlords.removing') }}: {{ removeLandlordName }} </h1>
      <p slot="body">{{ $t('landlords.confirm_remove') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ landlordWarningHeader }}</h1>
      <p slot="body">{{ landlordWarningBody }}</p>
    </WarningModal>
  </div>

</template>

<script>
import { mapGetters } from 'vuex';
import 'styles/vue-table.scss';
import * as mTypes from 'store/mutation-types';
import Autocomplete from 'common/Autocomplete';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    Autocomplete,
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    product: 'products/product/product',
    selectedProductId: 'products/product/selectedProductId',
    productsLimit: 'landlord/products/limit',
    productsKeywordFilter: 'landlord/products/keywordFilter',
    landlordsError: 'products/landlords/error',
    user: 'user/user/user',
  }),
  route: {
    canReuse: true,
  },
  watch: {
    product: 'refreshColumns',
  },
  created() {
    this.$validator.remove('cannotBeLower');
    this.createAmountValidator();
  },
  methods: {
    toggleForm() {
      this.isFormOpen = !this.isFormOpen;
      this.isActive = !this.isActive;
      if (this.isFormOpen) {
        this.$nextTick(() => {
          this.$el.getElementsByClassName('autocomplete__input')[0].focus();
        });
      } else {
        this.tenant = null;
        this.form.amount = null;
      }
    },
    selectLandlord(q) {
      this.landlord = q.item;
      this.$nextTick(() => {
        if (this.product.type === 'permitPack') {
          this.$refs.amount.focus();
        }
      });
    },
    autocompleteLandlordFilter(data) {
      return data;
    },
    addLandlord() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.$store.dispatch('products/landlords/shared', {
            landlordId: this.landlord.id,
            productId: this.product.id,
            amount: this.form.amount,
            user: this.user,
          })
            .then(() => {
              if (this.landlordsError) {
                this.displayError();
              } else {
                this.fetchProduct();
                this.fetchProductList();
                this.toggleForm();
              }
            })
            .catch(() => {});
        });
    },
    displayError() {
      this.landlordWarningHeader = this.$t('common.error');
      this.showWarningModal = true;
      switch (this.productsError) {
        case 'invalid_product_amount':
          this.landlordWarningBody = this.$t('errors.invalid_product_amount');
          break;
        case 'product_amount_lower_than_permits_count':
          this.landlordWarningBody = this.$t('errors.product_amount_lower_than_permits_count');
          break;
        default:
          this.landlordWarningBody = this.$t('errors.zone_capacity_exceeded');
      }
    },
    removeShared() {
      this.showConfirmationModal = false;
      this.$store.dispatch('products/landlords/delete', {
        landlordId: this.removeLandlordId,
        productId: this.product.id,
        user: this.user,
      });
      this.removeLandlordId = null;
      this.fetchProduct();
      this.fetchProductList();
    },
    showConfirmationForRemovingLandlord(landlord) {
      if (parseInt(landlord.permits, 10) > 0) {
        this.landlordWarningHeader = `${this.$t('tenants.removing')}:  ${landlord.name}`;
        this.landlordWarningBody = `${this.$t('common.issued_landlords')} ${this.$t('landlords.cannot_remove')}`;
        this.showWarningModal = true;
      } else {
        this.removeLandlordId = landlord.id;
        this.removeLandlordName = landlord.name;
        this.showConfirmationModal = true;
      }
    },
    cancelLandlordRemove() {
      this.showConfirmationModal = false;
      this.removeLandlordId = null;
      this.removeLandlordName = null;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`products/landlords/${mTypes.DATA_ERROR}`, false);
    },
    submitEdit(landlordId) {
      if (this.oldValue.toString() === this.$refs[`changeIssued${landlordId}`].value) {
        this.setSelectedRow(null);
        return false;
      }
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.$store.dispatch('products/landlords/edit', {
            landlordId,
            productId: this.product.id,
            amount: this.permitInput,
            user: this.user,
          })
            .then(() => {
              if (this.tenantsError) {
                this.displayError();
              } else {
                this.fetchProduct();
                this.fetchProductList();
              }
            })
            .catch(() => {
            });
        });
      this.setSelectedRow(null);
      return false;
    },
    editTotalPermitAmount(prop, value) {
      if (value >= 0) {
        this[prop] = value;
        this.permit_value = this[prop];
      }
    },
    fetchProduct() {
      this.$store.dispatch('products/product/fetch', { id: this.$route.params.id, user: this.user });
    },
    fetchProductList() {
      this.$store.dispatch('landlord/products/fetch', { user: this.user, limit: this.productsLimit, keyword: this.productsKeywordFilter });
    },
    setSelectedRow(row) {
      const rowId = row && row.id ? row.id : row;
      this.selectedRow.id = rowId;
      this.selectedRow.productId = this.product.id;
      this.$nextTick(() => {
        if (this.isSelectedRow(rowId) && rowId) {
          this.$refs[`changeIssued${rowId}`].focus();
        }
      });
      this.oldValue = row && row.issued ? row.issued : row;
    },
    isSelectedRow(rowId) {
      if (this.selectedRow.productId !== this.product.id) {
        this.setSelectedRow(null);
      }
      return this.selectedRow.id === rowId;
    },
    createAmountValidator() {
      this.$validator.extend('cannotBeLower', {
        getMessage: () => 'Number cannot be lower then assigned permits',
        validate: (value) => {
          if (this.product.type === 'permitPack' && value < 1) {
            return false;
          }
          return true;
        },
      });
    },
    refreshColumns() {
      this.columns = this.product.type === 'permitPack' ?
        ['name', 'contactPerson', 'address', 'issued', 'removeCompany', 'edit'] :
        ['name', 'contactPerson', 'address', 'removeCompany', 'edit'];
    },
    routeToLandlord(landlord) {
      this.$router.push({ name: 'landlord', params: { id: landlord.id }, query: { keyword: landlord.name } });
    },
  },
  data() {
    return {
      showConfirmationModal: false,
      showWarningModal: false,
      removeLandlordId: null,
      removeLandlordName: null,
      isActive: false,
      permitInput: null,
      oldValue: null,
      selectedRow: {
        id: null,
        productId: null,
      },
      landlord: null,
      isFormOpen: false,
      form: {},
      columns: ['name', 'contactPerson', 'address', 'issued', 'removeCompany', 'edit'],
      options: {
        texts: {
          noResults: this.$t('tenants.no_matching_records'),
          count: this.$t('tables.count'),
        },
        filterable: false,
        rowClassCallback: () => 'VueTables__row',
        headings: {
          name: () => this.$t('common.name'),
          contactPerson: () => this.$t('tenants.contact_person'),
          address: () => this.$t('common.address'),
          issued: () => this.$t('common.issued'),
          removeCompany: () => (''),
          edit: () => (''),
        },
        perPage: 10,
      },
    };
  },
};
</script>
<style lang="scss" scoped>
  @import '~styles/blocks/details.scss';
</style>




// WEBPACK FOOTER //
// src/components/products/LandlordsTable.vue